<!-- Component can be used in microscope and periscope -->
<template>
  <article
    v-if="message || helpText"
    class="conversation-message"
    role="listitem"
    :class="{
      sent: alignment === 'sent',
      received: alignment === 'received',
      deleted: !!message?.deletedAt,
    }"
  >
    <header v-if="message" class="message-header">
      <div class="message-header-main">
        <span v-if="isUnreadForReader" class="unread-dot" title="Oläst" aria-label="Oläst" />
        <span class="message-author">{{ message.author.displayName }}</span>
        <span class="message-organisation">{{ message.author.organisationName }}</span>
      </div>

      <div class="message-header-meta">
        <time :datetime="message.createdAt">{{ createdAtLabel }}</time>
        <span v-if="showReadState" class="message-read-state">{{ readStateLabel }}</span>
      </div>
    </header>

    <div v-if="message && replyToMessage" class="reply-preview">
      <span class="reply-preview-label">Svar på</span>
      <p class="reply-preview-message">
        {{ replyPreviewText }}
      </p>
    </div>

    <p class="message-body">
      {{ bodyText }}
    </p>

    <footer v-if="message && hasActions" class="message-actions">
      <AppButton v-if="canReply" type="button" color="transparent" small @click="emit('reply', message)">
        Svara
      </AppButton>
      <AppButton v-if="canEdit" type="button" color="transparent" small @click="emit('edit', message)">
        Redigera
      </AppButton>
      <AppButton v-if="canDelete" type="button" color="transparent" small @click="emit('delete', message)">
        Ta bort
      </AppButton>
      <AppButton v-if="canMarkRead" type="button" color="transparent" small @click="emit('mark-read', message)">
        {{ markReadLabel }}
      </AppButton>
    </footer>
  </article>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { ConversationMessage as ConversationMessageType } from '@staff/shared'
import AppButton from '../AppButton/AppButton.vue'

const props = withDefaults(
  defineProps<{
    message?: ConversationMessageType
    helpText?: string
    alignment?: 'sent' | 'received'
    replyToMessage?: ConversationMessageType | null
    canReply?: boolean
    canEdit?: boolean
    canDelete?: boolean
    canMarkRead?: boolean
    showReadState?: boolean
  }>(),
  {
    message: undefined,
    helpText: undefined,
    alignment: 'received',
    replyToMessage: null,
    canReply: false,
    canEdit: false,
    canDelete: false,
    canMarkRead: false,
    showReadState: true,
  },
)

const emit = defineEmits<{
  (e: 'reply', message: ConversationMessageType): void
  (e: 'edit', message: ConversationMessageType): void
  (e: 'delete', message: ConversationMessageType): void
  (e: 'mark-read', message: ConversationMessageType): void
}>()

const formatDateTime = (value: string | null) => {
  if (!value) return null

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return new Intl.DateTimeFormat('sv-SE', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)
}

const createdAtLabel = computed(() => formatDateTime(props.message?.createdAt ?? null) ?? props.message?.createdAt)

const readStateLabel = computed(() => {
  if (props.message?.readAt) {
    return `Läst ${formatDateTime(props.message.readAt)}`
  }

  return 'Ej läst'
})

const replyPreviewText = computed(() => {
  if (!props.replyToMessage) return ''
  if (props.replyToMessage.deletedAt) return 'Meddelandet har tagits bort'
  return props.replyToMessage.message
})

const bodyText = computed(() =>
  props.message
    ? props.message.deletedAt
      ? 'Det här meddelandet har tagits bort.'
      : props.message.message
    : props.helpText,
)

// Read state belongs to the receiving side, so only a received message can be unread for the reader.
const isUnreadForReader = computed(
  () => props.alignment === 'received' && !!props.message && !props.message.readAt && !props.message.deletedAt,
)

const markReadLabel = computed(() => (props.message?.readAt ? 'Markera oläst' : 'Markera läst'))

const hasActions = computed(() => props.canReply || props.canEdit || props.canDelete || props.canMarkRead)
</script>

<style scoped lang="scss">
.conversation-message {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 10px;
  max-width: min(40rem, 100%);
  padding: 14px 16px;
  border-radius: 16px;
  background-color: var(--color-canvas-subtle);
  color: var(--color-fg-default);

  &.sent {
    align-self: flex-end;
    background-color: var(--color-success-subtle);

    .reply-preview {
      border-color: rgba(255, 255, 255, 0.18);
    }
  }

  &.received {
    align-self: flex-start;
    background-color: var(--color-canvas-subtle);
  }

  &.deleted {
    opacity: 0.8;
  }
}

.message-header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 12px;
}

.message-header-main,
.message-header-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.unread-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--color-accent-emphasis);
}

.message-author {
  font-size: 0.875rem;
  font-weight: 600;
}

.message-organisation,
.message-header-meta,
.reply-preview-label {
  font-size: 0.75rem;
  color: var(--color-fg-subtle);
}

.reply-preview {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  border-radius: 10px;
  background-color: var(--color-canvas-translucent);
}

.reply-preview-message,
.message-body {
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  line-height: 1.5;
}

.message-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
</style>
